<script setup lang="ts">
import { useCart } from '@b2b-platform/commerce/composables/useCart';
import { formatPrice } from '@b2b-platform/commerce/utils/format';

const {
    cart,
    deleteLineItem,
    newItem,
    closeCart,
    isCartOpen,
    disableDeleteButton,
} = useCart();
import {
    Popover,
    PopoverButton,
    PopoverOverlay,
    PopoverPanel,
} from '@headlessui/vue';

defineProps({
    asTextButton: {
        default: false,
        type: Boolean,
    },
});
</script>

<template>
    <Popover class="relative">
        <button title="Warenkorb" class="relative">
            <PopoverButton
                v-if="asTextButton"
                class="w-full justify-center text-sm font-medium focus:outline-none focus-visible:ring-2"
            >
                <JamBaseButton
                    icon-name="cart"
                    label="Warenkorb"
                    variant="gray"
                />
                <BaseBadge
                    v-if="cart && cart.totalQuantity > 0"
                    variant="red"
                    rounded="rounded"
                    size="standard"
                    class="absolute left-7 top-1"
                >
                    {{ cart.totalQuantity }}
                </BaseBadge>
            </PopoverButton>
            <PopoverButton
                v-else
                class="w-full justify-center p-3 text-sm font-medium focus:outline-none focus-visible:ring-2"
            >
                <BaseIcon icon-name="cart" size="large"></BaseIcon>

                <BaseBadge
                    v-if="cart && cart.totalQuantity > 0"
                    variant="red"
                    rounded="rounded"
                    size="standard"
                    class="absolute right-1 top-1"
                >
                    {{ cart.totalQuantity }}
                </BaseBadge>
            </PopoverButton>
        </button>
        <PopoverOverlay
            :static="isCartOpen"
            class="fixed inset-0 z-10 bg-black opacity-40"
            @click="closeCart()"
        />
        <Transition
            enter-active-class="transition-transform transition-opacity duration-300 ease-out"
            enter-from-class="transform translate-x-full opacity-0"
            enter-to-class="transform translate-x-0 opacity-100"
            leave-active-class="transition-transform transition-opacity duration-200 ease-in"
            leave-from-class="transform translate-x-0 opacity-100"
            leave-to-class="transform translate-x-full opacity-0"
        >
            <PopoverPanel
                class="fixed !right-0 !top-0 z-[50] flex h-full min-h-[300px] w-full flex-col gap-4 bg-white p-6 shadow md:inset-auto md:w-[480px]"
                :static="isCartOpen"
            >
                <div class="flex items-center justify-between">
                    <BaseHeading variant="lg-500">Warenkorb</BaseHeading>

                    <PopoverButton
                        class="absolute right-0 top-0 m-2 cursor-pointer p-4 text-gray-600 hover:text-gray-800"
                        title="Warenkorb schließen"
                        @click="closeCart"
                    >
                        <JamBaseIcon icon-name="close" size="large" />
                    </PopoverButton>
                </div>
                <div
                    v-if="newItem"
                    class="my-4 flex gap-3"
                    aria-label="Produkt wurde zum Warenkorb hinzugefügt"
                >
                    <JamBaseIcon icon-name="check" size="large" class="text-green-700" />
                    <BaseText variant="base-500">
                        Produkt wurde zum Warenkorb hinzugefügt
                    </BaseText>
                </div>
                <div
                    v-if="
            cart &&
            (cart.lineItems?.data.length > 0 ||
              cart.jobAdLineItems?.data.length > 0)
          "
                    class="flex h-full flex-col justify-between overflow-y-auto"
                >
                    <div
                        class="mb-12 flex flex-1 flex-col justify-between space-y-3 overflow-y-auto px-1 pt-2"
                    >
                        <ul class="flex flex-col gap-3">
                            <li
                                v-for="lineItem in cart.lineItems.data"
                                :key="lineItem"
                                class="flex w-full flex-col gap-2 bg-blue-100 p-3"
                            >
                                <div class="flex justify-between">
                                    <BaseText variant="base-500"
                                              class="w-[250px] sm:w-full"
                                    >
                                        {{ lineItem.quantity }}x
                                        {{ lineItem.name }}
                                        <button
                                            :disabled="disableDeleteButton"
                                            :class="[
                                          'ml-1 inline-flex translate-y-[5px]',
                                          disableDeleteButton && 'cursor-not-allowed',
                                          'hover:text-gray-600',
                                        ]"
                                            @click="deleteLineItem(lineItem.id)"
                                        >
                                            <JamBaseIcon icon-name="trash-can" size="large" />
                                        </button>
                                    </BaseText>

                                    <BaseText variant="base-500">
                                        {{ formatPrice(lineItem.price.amount * lineItem.quantity) }}
                                    </BaseText>
                                </div>

                                <ul
                                    v-if="lineItem.addons?.data.length > 0"
                                    class="flex flex-col gap-2"
                                >
                                    <li
                                        v-for="addon in lineItem.addons?.data"
                                        :key="addon"
                                        class="flex justify-between"
                                    >
                                        <BaseText variant="sm-400">
                                            + {{ addon.name }}
                                            <button
                                                :disabled="disableDeleteButton"
                                                :class="[
                          'ml-1 inline-flex translate-y-[5px]',
                          disableDeleteButton && 'cursor-not-allowed',
                          'hover:text-gray-600',
                        ]"
                                                @click="deleteLineItem(addon.id)"
                                            >
                                                <JamBaseIcon
                                                    icon-name="trash-can"
                                                    size="large"
                                                    class="hover:text-gray-600"
                                                />
                                            </button>
                                        </BaseText>

                                        <BaseText variant="sm-500" class="content-center">
                                            {{ formatPrice(addon.price.amount * lineItem.quantity) }}
                                        </BaseText>
                                    </li>
                                </ul>
                            </li>
                            <li class="w-full">
                                <ul
                                    v-if="
                    cart.jobAdLineItems && cart.jobAdLineItems.data.length > 0
                  "
                                >
                                    <li
                                        v-for="lineItem in cart.jobAdLineItems.data"
                                        :key="lineItem"
                                        class="mb-4 w-full bg-blue-100 p-3"
                                    >
                                        <div class="mb-2 flex justify-between">
                                            <BaseText variant="base-500">
                                                {{ lineItem.quantity }}x
                                                {{ lineItem.name }}
                                                <button
                                                    :disabled="disableDeleteButton"
                                                    :class="[
                            'ml-1 inline-flex translate-y-[5px]',
                            disableDeleteButton && 'cursor-not-allowed',
                            'hover:text-gray-600',
                          ]"
                                                    @click="deleteLineItem(lineItem.id)"
                                                >
                                                    <JamBaseIcon
                                                        icon-name="trash-can"
                                                        size="large"
                                                        class="hover:text-gray-600"
                                                    />
                                                </button>
                                            </BaseText>
                                            <BaseText variant="base-500">
                                                {{ formatPrice(lineItem.price.amount) }}
                                            </BaseText>
                                        </div>
                                        <BaseText variant="sm-400">
                                            - Inserat Job-ID
                                            <BaseText variant="sm-500" as="span">
                                                {{ cart.jobId }}
                                            </BaseText>
                                        </BaseText>
                                    </li>
                                </ul>
                            </li>
                        </ul>
                        <ul
                            v-if="cart.hasJobAdProduct"
                            class="flex flex-col gap-1 bg-blue-100 p-3"
                        >
                            <li class="w-full">
                                <!-- smart bewerben -->
                                <div class="flex justify-between">
                                    <CommBaseCollapse trigger-classes="!justify-start">
                                        <template #trigger>
                                            <BaseText variant="base-500"> smart bewerben</BaseText>
                                        </template>
                                        <BaseText variant="sm-400" color="text-gray-600">
                                            Ein einheitlicher Bewerbungsprozess bietet Talenten einen
                                            klaren, transparenten Bewerbungsweg. Sie sparen Zeit und
                                            finden alle Bewerbungen übersichtlich strukturiert in
                                            Ihrem Bewerbermanagement-System.
                                        </BaseText>
                                    </CommBaseCollapse>
                                    <BaseText variant="base-500"> Inklusive</BaseText>
                                </div>
                            </li>
                            <li class="w-full">
                                <!-- bewerber.manager -->
                                <div class="flex justify-between">
                                    <CommBaseCollapse trigger-classes="!justify-start">
                                        <template #trigger>
                                            <BaseText variant="base-500"> bewerber.manager</BaseText>
                                        </template>
                                        <BaseText variant="sm-400" color="text-gray-600">
                                            Der bewerber.manager ist ein Tool zur effizienten und
                                            gesetzeskonformen Verwaltung von Bewerbungen, umfasst die
                                            Basisfunktionen eines Bewerbermanagementsystems und ist
                                            für karriere.at Kunden inklusive. Wenn Sie den
                                            bewerber.manager nutzen wollen, müssen Sie ihn aktivieren
                                            lassen. Kontaktieren Sie uns hierzu bitte unter
                                            <a
                                                href="mailto:shop-support@karriere.at"
                                                class="font-semibold underline"
                                            >shop-support@karriere.at</a
                                            >.
                                        </BaseText>
                                    </CommBaseCollapse>
                                    <BaseText variant="base-500"> Inklusive</BaseText>
                                </div>
                            </li>
                            <li v-if="!cart.hasEmployerProfileProduct" class="w-full">
                                <!-- employer.page -->
                                <div class="flex justify-between">
                                    <CommBaseCollapse trigger-classes="!justify-start">
                                        <template #trigger>
                                            <BaseText variant="base-500"> employer.page</BaseText>
                                        </template>
                                        <BaseText variant="sm-400" color="text-gray-600">
                                            Ihr Arbeitgeberprofil direkt auf karriere.at. Präsentieren
                                            Sie Talenten die wichtigsten Informationen zu Ihrem
                                            Unternehmen direkt auf karriere.at.
                                        </BaseText>
                                    </CommBaseCollapse>
                                    <BaseText variant="base-500"> Inklusive</BaseText>
                                </div>
                            </li>
                        </ul>
                    </div>
                    <div class="sticky bottom-0 bg-white">
                        <div v-if="cart.discountCode">
                            <div v-for="(discount, idx) in cart.discountCode" :key="idx">
                                <div v-if="discount.amount.amount > 0">
                                    <hr class="mt-3" />
                                    <div class="mt-3 flex justify-between">
                                        <BaseText variant="lg-400">
                                            Gutscheincode „{{ discount.code }}“
                                        </BaseText>
                                        <BaseText variant="lg-400" class="text-red-700">
                                            -
                                            {{ formatPrice(discount.amount.amount) }}
                                        </BaseText>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div v-else class="mt-4">
                            <BaseText variant="sm-400" color="text-gray-600">
                                Sie haben einen Gutscheincode? Lösen Sie ihn im letzten Schritt
                                ein.
                            </BaseText>
                        </div>
                        <hr class="mt-3" />
                        <div class="mt-3 flex justify-between">
                            <BaseText variant="lg-500"> Gesamt (exkl. Ust.)</BaseText>
                            <BaseText variant="lg-500">
                                {{ formatPrice(cart.totalPriceWithoutTaxes.amount) }}
                            </BaseText>
                        </div>
                        <div class="mt-5 gap-3 md:flex">
                            <PopoverButton
                                class="w-full"
                                as="a"
                                href="/#products"
                                @click="closeCart"
                            >
                                <JamBaseButton variant="outline" class="w-full">
                                    Weiter einkaufen
                                </JamBaseButton>
                            </PopoverButton>
                            <JamBaseButton
                                class="mt-4 w-full md:mt-0"
                                :to="`${
                  useRuntimeConfig().public.businessPortal.url
                }/shop/checkout`"
                                @click="closeCart"
                            >
                                Kauf abschließen
                            </JamBaseButton>
                        </div>
                    </div>
                </div>
                <div v-else class="mt-6 flex flex-col items-center">
                    <img
                        src="/p/icons/base-shopping-cart.svg"
                        alt="Empty Cart"
                        class="h-20 w-20"
                    />
                    <p class="mt-4 text-gray-600">Ihr Warenkorb ist aktuell noch leer.</p>
                    <p class="text-gray-600">Fügen Sie Produkte hinzu.</p>
                    <div class="mt-6 text-center">
                        <div>
                            <PopoverButton as="a" href="/#products" @click="closeCart">
                                <JamBaseLink
                                    variant="dark-gray"
                                    stroke-size="thin"
                                    class="mt-3"
                                >
                                    Jetzt Produkte entdecken
                                </JamBaseLink>
                            </PopoverButton>
                        </div>
                    </div>
                </div>
            </PopoverPanel>
        </Transition>
    </Popover>
</template>
